/**
 * dashboardUtils.js
 * Lógica de transformación de datos del dashboard.
 * No modifica la estructura original del response.
 */

/**
 * Convierte un string de monto "$123,456.78" a número.
 * Si no es convertible, retorna 0.
 */
export const parseMonto = (str) => {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  const cleaned = String(str).replace(/[$,]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/**
 * Formatea un número como monto en MXN.
 */
export const formatMonto = (value) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

/**
 * Genera la lista dinámica de meses desde Enero 2026 hasta el mes actual.
 * @returns {Array<{ label: string, mes: number, anio: number }>}
 */
export const generarListaMeses = () => {
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const inicio = { anio: 2026, mes: 1 };
  const ahora = new Date();
  const fin = { anio: ahora.getFullYear(), mes: ahora.getMonth() + 1 };

  const lista = [];
  let { anio, mes } = inicio;

  while (anio < fin.anio || (anio === fin.anio && mes <= fin.mes)) {
    lista.push({
      label: `${meses[mes - 1]} ${anio}`,
      mes,
      anio,
    });
    mes++;
    if (mes > 12) {
      mes = 1;
      anio++;
    }
  }

  return lista;
};

/**
 * Obtiene el mes y año actuales como valor por defecto.
 */
export const getMesActual = () => {
  const ahora = new Date();
  return { mes: ahora.getMonth() + 1, anio: ahora.getFullYear() };
};

/**
 * Construye datos para la gráfica de barras: Monto total por sucursal.
 * @param {Array} sucursales - Arreglo 1: [{ ID_SUC, NOMBRE_SUCURSAL_SUC }]
 * @param {Array} metricasSuc - Arreglo 2: [{ ID_SUC, MONTO_TOTAL_SUC, ... }]
 */
export const buildMontoSucursalData = (sucursales = [], metricasSuc = []) => {
  const labels = sucursales.map((s) => s.NOMBRE_SUCURSAL_SUC);
  const data = sucursales.map((s) => {
    const metrica = metricasSuc.find((m) => m.ID_SUC === s.ID_SUC);
    return metrica ? parseMonto(metrica.MONTO_TOTAL_SUC) : 0;
  });
  return { labels, data };
};

/**
 * Construye datos para la gráfica de barras: Escaneos por sucursal.
 * @param {Array} sucursales
 * @param {Array} metricasSuc
 */
export const buildEscaneosSucursalData = (sucursales = [], metricasSuc = []) => {
  const labels = sucursales.map((s) => s.NOMBRE_SUCURSAL_SUC);
  const data = sucursales.map((s) => {
    const metrica = metricasSuc.find((m) => m.ID_SUC === s.ID_SUC);
    return metrica ? (metrica.ESCANEOS_SUC || 0) : 0;
  });
  return { labels, data };
};

/**
 * Construye datos para la gráfica donut: Ingreso Final vs Descuento.
 * Solo 2 secciones que deben sumar 100%.
 * @param {Array} metricasGen - Arreglo 3: [{ MONTO_TOTAL, DESCUENTO, MONTO_FINAL, ... }]
 */
export const buildDonutMetricasGen = (metricasGen = []) => {
  if (!metricasGen.length) return { labels: [], data: [] };
  const gen = metricasGen[0];
  return {
    labels: ['INGRESO FINAL', 'DESCUENTO'],
    data: [
      parseMonto(gen.MONTO_FINAL),
      parseMonto(gen.DESCUENTO),
    ],
  };
};

/**
 * Construye datos para la gráfica de barras apiladas: Cupones por sucursal.
 * @param {Array} sucursales
 * @param {Array} cuponesSuc - Arreglo 4: [{ ID_SUC_ESC, TITULO_CUP, TOTAL, ... }]
 */
export const buildCuponesSucursalStacked = (sucursales = [], cuponesSuc = []) => {
  // Recopilar todos los títulos únicos de cupones
  const titulosSet = new Set(cuponesSuc.map((c) => c.TITULO_CUP));
  const titulos = Array.from(titulosSet);

  const labels = sucursales.map((s) => s.NOMBRE_SUCURSAL_SUC);

  // Cada serie representa un tipo de cupón
  const series = titulos.map((titulo) => {
    const dataPoints = sucursales.map((s) => {
      const cupon = cuponesSuc.find(
        (c) => c.ID_SUC_ESC === s.ID_SUC && c.TITULO_CUP === titulo
      );
      return cupon ? (cupon.TOTAL || 0) : 0;
    });
    return { name: titulo, data: dataPoints };
  });

  return { labels, series };
};

/**
 * Construye datos para la gráfica de pie: Distribución de cupones generales.
 * @param {Array} cuponesGen - Arreglo 5: [{ TITULO_CUP, TOTAL, ... }]
 */
export const buildCuponesGeneralesPie = (cuponesGen = []) => {
  const labels = cuponesGen.map((c) => c.TITULO_CUP);
  const data = cuponesGen.map((c) => c.TOTAL || 0);
  return { labels, data };
};

/**
 * Construye datos para la gráfica de pie: Origen de clientes.
 * @param {Array} origenClientes - Arreglo 6: [{ RESPUESTA_DP, TOTAL }]
 */
export const buildOrigenClientesPie = (origenClientes = []) => {
  const labels = origenClientes.map((o) => o.RESPUESTA_DP);
  const data = origenClientes.map((o) => o.TOTAL || 0);
  return { labels, data };
};

/**
 * Construye datos para la gráfica de barras: Escaneos por mesero.
 * @param {Array} meseros - [{ MESERO, SUCURSAL, TOTAL_ESCANEOS }]
 */
export const buildMeserosBar = (meseros = []) => {
  const labels = meseros.map((m) => m.MESERO);
  const data = meseros.map((m) => m.TOTAL_ESCANEOS || 0);
  const sucursales = meseros.map((m) => m.SUCURSAL);
  return { labels, data, sucursales };
};

/**
 * Construye datos para la gráfica de barras: Meseros más mencionados en registros.
 * @param {Array} meserosRegistro - [{ MESERO_COMENTARIO, TOTAL }]
 */
export const buildMeserosRegistroBar = (meserosRegistro = []) => {
  const labels = meserosRegistro.map((m) => m.MESERO_COMENTARIO);
  const data = meserosRegistro.map((m) => m.TOTAL || 0);
  return { labels, data };
};
